import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ChevronDown, Check, Plus } from "lucide-react";
import { useState } from "react";
import { useChangeOrganisation } from "../../../_features/auth/hooks/useChangeOrganisation";
import CreateOrganisationModal from "../../../_features/auth/modals/CreateOrganisationModal";
import { usePermissionCheck } from "../../../_hooks/usePermissionCheck";
import { paths } from "../../../_app/router/paths";
import { persistor } from "../../../store";
import "./NavOrgContainer.scss";

const isOwnerOfOrg = (org, userId) =>
  org?.orgId?.users?.find((u) => u?.user === userId)?.role === "owner";

const NavOrgContainer = () => {
  const [isOrgOpen, setIsOrgOpen] = useState(true);
  const [isCreateOrgOpen, setIsCreateOrgOpen] = useState(false);
  const navigate = useNavigate();
  const { canAction } = usePermissionCheck();
  const canCreateOrg = canAction("organisation", "create");

  const { _id: userId, organisations, selectedOrg } = useSelector(
    (state) => state?.auth?.user
  );

  const { changeOrganisation } = useChangeOrganisation();

  // A membership stuck on "invite sent" means this user hasn't accepted
  // their invite yet - don't let them switch into (or even see) that org
  // from the nav until they do. Owners are exempt: they're shown their own
  // org regardless, since it's how they get back in to finish onboarding.
  const visibleOrganisations = organisations?.filter(
    (org) =>
      isOwnerOfOrg(org, userId) || org?.status?.toLowerCase() !== "invite sent"
  );

  const handleChangeOrganisation = async (e, org) => {
    e.stopPropagation();
    const orgId = org?.orgId?._id;
    try {
      await changeOrganisation(orgId, {
        onSuccess: async () => {
          if (isOwnerOfOrg(org, userId) && !org?.orgId?.isOnboarded) {
            navigate(paths.onboarding, { state: { orgId } });
          } else {
            // redux-persist writes to localStorage asynchronously - reloading
            // right after dispatch(setCredentials(...)) can race the write and
            // read back the PREVIOUS org's persisted snapshot, silently
            // reverting the switch. Flush first so the reload always picks up
            // the org we just switched to.
            await persistor.flush();
            window.location.reload();
          }
        },
      });
    } catch {
      // changeOrganisation already surfaced a toast.
    }
  };

  const handleToggleOrg = (e) => {
    e.stopPropagation();
    setIsOrgOpen((prev) => !prev);
  };

  return (
    <div className="org-container">
      <div className="org-container__header" onClick={(e) => handleToggleOrg(e)}>
        <p className="org-container__title">Switch organisation</p>
        {isOrgOpen ? (
          <ChevronDown className="org-container__arrow" size={14} />
        ) : (
          <ChevronRight className="org-container__arrow" size={14} />
        )}
      </div>
      {isOrgOpen && (
        <div className="org-container__items">
          {visibleOrganisations?.map((org) => {
            const isActive = org?.orgId?._id === selectedOrg;
            const className = isActive ? "org-container__item org-container__item--active" : "org-container__item";
            return (
              <p
                key={org?.orgId?._id}
                className={className}
                onClick={(e) => handleChangeOrganisation(e, org)}
              >
                <span>{org?.orgId?.orgName}</span>
                {isActive && <Check size={16} />}
              </p>
            );
          })}
          {canCreateOrg && (
            <p
              className="org-container__item org-container__item--add"
              onClick={(e) => {
                e.stopPropagation();
                setIsCreateOrgOpen(true);
              }}
            >
              <span>New organisation</span>
              <Plus size={14} />
            </p>
          )}
        </div>
      )}
      <CreateOrganisationModal isOpen={isCreateOrgOpen} onClose={() => setIsCreateOrgOpen(false)} />
    </div>
  );
};

export default NavOrgContainer;
